/**
 * TaskDetailsDialog Component
 * Modal dialog for viewing and editing task details.
 * Shows task info, status chip, and allows editing or deletion.
 */

import { useState, useEffect, useRef } from 'react';
import {
  Pencil,
  Trash2,
  Calendar,
  Clock,
  FileText,
  ExternalLink,
  ArrowRight,
  User,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from '@tanstack/react-router';
import type { Task } from '@signalvote/shared';
import { TaskStatus } from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTaskStore } from '@/stores';
import { useVolunteers, useUpdateTask, useAcknowledgeTask } from '@/hooks/queries';
import { useAuth } from '@/contexts/AuthContext';
import { useMayManageTasks } from '@/hooks';
import { formatVolunteerDisplayName, isExternalUrl } from '@/lib/taskAssignmentUtils';

// ============================================================================
// Types
// ============================================================================

interface TaskDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  task: Task | null;
  onDelete: (task: Task) => void;  // Called instead of direct delete for undo support
}

// ============================================================================
// Helper Functions
// ============================================================================

function getStatusColor(status: TaskStatus | string): string {
  switch (status) {
    case TaskStatus.Pending:
      return 'bg-amber-100 text-amber-800';
    case TaskStatus.Accepted:
      return 'bg-blue-100 text-blue-800';
    case TaskStatus.Declined:
      return 'bg-red-100 text-red-800';
    case TaskStatus.Completed:
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function getStatusLabel(status: TaskStatus | string): string {
  switch (status) {
    case TaskStatus.Pending:
      return 'Pending';
    case TaskStatus.Accepted:
      return 'Accepted';
    case TaskStatus.Declined:
      return 'Declined';
    case TaskStatus.Completed:
      return 'Completed';
    default:
      return String(status);
  }
}

function formatDateForDisplay(dateStr: string): string {
  const date = parseISO(dateStr);
  return format(date, 'EEEE, MMMM d, yyyy');
}

function formatTimeForDisplay(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  return `${hours}h ${mins}m`;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Shell: owns nothing but the dialog frame. The body is remounted per task id,
 * and Radix unmounts it on close, so the edit form is seeded exactly once per
 * "the user opened this task" — a background refetch of the task can no longer
 * reach in and clobber an edit in progress.
 */
export function TaskDetailsDialog({
  open,
  onClose,
  task,
  onDelete,
}: TaskDetailsDialogProps) {
  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <TaskDetailsDialogBody
          key={task.id}
          task={task}
          onClose={onClose}
          onDelete={onDelete}
        />
      </DialogContent>
    </Dialog>
  );
}

function TaskDetailsDialogBody({
  task,
  onClose,
  onDelete,
}: {
  task: Task;
  onClose: () => void;
  onDelete: (task: Task) => void;
}) {
  const updateTaskStore = useTaskStore((state) => state.updateTask);
  const updateTaskApi = useUpdateTask();
  const navigate = useNavigate();
  const { data: volunteers = [] } = useVolunteers();
  const { user } = useAuth();
  const { mutate: acknowledgeTask } = useAcknowledgeTask();
  const mayManageTasks = useMayManageTasks();

  const [isEditing, setIsEditing] = useState(false);

  // Edit form state, seeded once from the task this body was mounted for.
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [dueDate, setDueDate] = useState(task.dueDate || '');
  const [dueTime, setDueTime] = useState(task.dueTime || '');
  const [duration, setDuration] = useState(task.durationMinutes || 60);
  const [assigneeId, setAssigneeId] = useState(task.assignedToId || '');

  function resetFormToTask(t: Task): void {
    setTitle(t.title);
    setDescription(t.description);
    setDueDate(t.dueDate || '');
    setDueTime(t.dueTime || '');
    setDuration(t.durationMinutes || 60);
    setAssigneeId(t.assignedToId || '');
  }

  // Auto-acknowledge an unacknowledged inbound task. The deps name every value
  // read, so a `user` that resolves after the dialog opened still acknowledges;
  // the ref keeps a re-run (a refetch lands a new task object before the
  // acknowledge round-trips) from firing a second POST.
  const acknowledgedRef = useRef(false);
  useEffect(() => {
    if (acknowledgedRef.current || !user) return;
    if (
      task.assignedToId === user.id &&
      task.createdById !== user.id &&
      !task.acknowledgedAt
    ) {
      acknowledgedRef.current = true;
      acknowledgeTask(task.id);
    }
  }, [task, user, acknowledgeTask]);

  const handleDelete = () => {
    onDelete(task);
    onClose();
  };

  const handleSave = () => {
    if (!title.trim()) return;

    const selectedVolunteer = volunteers.find((v) => v.userId === assigneeId);
    const assigneeName = selectedVolunteer
      ? formatVolunteerDisplayName(selectedVolunteer.firstName, selectedVolunteer.lastName)
      : undefined;

    // Optimistic update in Zustand
    updateTaskStore(task.id, {
      title: title.trim(),
      description: description.trim(),
      dueDate: dueDate || undefined,
      dueTime: dueDate && dueTime ? dueTime : undefined,
      durationMinutes: duration || 60,
      assignedToId: assigneeId || undefined,
      assignedToName: assigneeName,
    });

    // Persist to backend. The endpoint reads a missing field as "keep", so a
    // blanked field has to be cleared with its flag.
    const nextDueTime = dueDate && dueTime ? dueTime : null;
    updateTaskApi.mutate({
      id: task.id,
      data: {
        title: title.trim(),
        description: description.trim(),
        dueDate: dueDate || null,
        clearDueDate: !dueDate,
        dueTime: nextDueTime,
        clearDueTime: !nextDueTime,
        durationMinutes: duration || 60,
        assignedToId: assigneeId || null,
        clearAssignee: !assigneeId,
      },
    });

    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    resetFormToTask(task);
    setIsEditing(false);
  };

  // Edit mode content
  if (isEditing) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
          {/* Title - Full Width */}
          <div className="col-span-2">
            <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Description - Full Width */}
          <div className="col-span-2">
            <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-1.5">
              Description
            </label>
            <textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
            />
          </div>

          {/* Due Date */}
          <div>
            <label htmlFor="edit-dueDate" className="block text-sm font-medium text-gray-700 mb-1.5">
              Due Date
            </label>
            <input
              id="edit-dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Due Time - Only shown when date is set */}
          {dueDate && (
            <div>
              <label htmlFor="edit-dueTime" className="block text-sm font-medium text-gray-700 mb-1.5">
                Due Time
              </label>
              <input
                id="edit-dueTime"
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          )}

          {/* Duration */}
          <div className={dueDate ? '' : 'col-span-1'}>
            <label htmlFor="edit-duration" className="block text-sm font-medium text-gray-700 mb-1.5">
              Duration
            </label>
            <div className="relative">
              <input
                id="edit-duration"
                type="number"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                min={15}
                max={480}
                step={15}
                className="w-full px-3 py-2 pr-16 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                minutes
              </span>
            </div>
          </div>

          {/* Assignee */}
          <div className="col-span-2">
            <label htmlFor="edit-assignee" className="block text-sm font-medium text-gray-700 mb-1.5">
              Assign to
            </label>
            <select
              id="edit-assignee"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="">Unassigned</option>
              {volunteers
                .filter((v) => v.userId !== user?.id)
                .map((v) => (
                  <option key={v.userId} value={v.userId}>
                    {v.firstName} {v.lastName}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!title.trim()}>
            Save Changes
          </Button>
        </DialogFooter>
      </>
    );
  }

  // View mode content
  return (
    <>
      {/* pr-10 keeps the title clear of the dialog's absolutely-positioned ✕. */}
      <DialogHeader className="pr-10">
        <DialogTitle className="text-lg leading-snug">{task.title}</DialogTitle>
        <span className={`inline-flex w-fit items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
          {getStatusLabel(task.status)}
        </span>
      </DialogHeader>

      <div className="py-4">
        {/* Description */}
        {task.description && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Description</span>
            </div>
            <p className="text-sm text-muted-foreground ml-6">
              {task.description}
            </p>
          </div>
        )}

        {/* Action URL button */}
        {task.actionUrl && (
          <div className="mb-4 ml-6">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => {
                if (isExternalUrl(task.actionUrl!)) {
                  window.open(task.actionUrl!, '_blank', 'noopener,noreferrer');
                } else {
                  navigate({ to: task.actionUrl! });
                  onClose();
                }
              }}
            >
              {isExternalUrl(task.actionUrl!) ? (
                <>
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open Link
                </>
              ) : (
                <>
                  <ArrowRight className="h-3.5 w-3.5" />
                  Go to Settings
                </>
              )}
            </Button>
          </div>
        )}

        <hr className="my-4 border-border" />

        {/* Due Date/Time */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            {task.dueDate ? (
              <span className="text-sm">
                {formatDateForDisplay(task.dueDate)}
                {task.dueTime && ` at ${formatTimeForDisplay(task.dueTime)}`}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">
                No due date set
              </span>
            )}
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              Estimated: {formatDuration(task.durationMinutes || 60)}
            </span>
          </div>

          {/* Assignee */}
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            {task.assignedToName ? (
              <span className="text-sm">
                Assigned to {task.assignedToName}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">
                Unassigned
              </span>
            )}
          </div>
        </div>
      </div>

      <DialogFooter className="flex-row justify-between sm:justify-between">
        {mayManageTasks ? (
          <Button
            variant="destructive"
            onClick={handleDelete}
            className="gap-1"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {mayManageTasks && (
            <Button
              variant="outline"
              onClick={() => setIsEditing(true)}
              className="gap-1"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </DialogFooter>
    </>
  );
}

export default TaskDetailsDialog;
